import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminApi, emailFlowApi } from '../services/api';
import { useAuthStore } from '../context/auth.store';
import FolderTreeView from '../components/explorer/FolderTreeView';
import toast from 'react-hot-toast';

function formatBytes(bytes, decimals = 1) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return dateStr;
  }
}

export default function CentralReportsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdminOrManager = ['ADMIN', 'MANAGER'].includes(user?.role);
  const isAdmin = user?.role === 'ADMIN';

  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [viewType, setViewType] = useState('table'); // 'table' or 'tree'
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedFormat, setSelectedFormat] = useState('ALL');
  const [flowFilter, setFlowFilter] = useState('ALL'); // 'ALL', 'LINKED', 'UNLINKED'
  const [selectedReportForFlow, setSelectedReportForFlow] = useState(null);
  const [triggeringFlow, setTriggeringFlow] = useState(false);

  // Archive & Historical Snapshot States
  const [archiveStatus, setArchiveStatus] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveProgress, setArchiveProgress] = useState(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState('LIVE');
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const fetchReports = async (force = false) => {
    try {
      if (force) setRefreshing(true);
      else setLoading(true);
      const res = await adminApi.centralReports(force);
      setReports(res.data.reports || []);
      setStats(res.data.stats || null);
    } catch (err) {
      console.error('Failed to load central reports:', err);
      toast.error('Failed to load central reports');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchArchiveStatus = async () => {
    try {
      const res = await adminApi.getArchiveStatus();
      setArchiveStatus(res.data);
      if (res.data.isSyncRunning) {
        setIsArchiving(true);
        setArchiveProgress(res.data.syncProgress);
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchReports();
    fetchArchiveStatus();
  }, []);

  // Poll archive progress if active
  useEffect(() => {
    let timer = null;
    if (isArchiving) {
      timer = setInterval(async () => {
        try {
          const res = await adminApi.getArchiveStatus();
          setArchiveStatus(res.data);
          if (res.data.isSyncRunning) {
            setArchiveProgress(res.data.syncProgress);
          } else {
            setIsArchiving(false);
            setArchiveProgress(null);
            toast.success('✅ Daily Local Archive Snapshot Completed Successfully!');
            fetchArchiveStatus();
          }
        } catch (e) {
          setIsArchiving(false);
        }
      }, 1500);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isArchiving]);

  const handleRunArchiveSync = async () => {
    setIsArchiving(true);
    setShowArchiveModal(true);
    try {
      await adminApi.triggerArchiveSync();
      toast.success('Initiating Daily Local Archive Download...');
    } catch (err) {
      toast.error('Failed to start archive sync');
      setIsArchiving(false);
    }
  };

  const handleSelectSnapshot = async (snapshotDate) => {
    setSelectedSnapshot(snapshotDate);
    if (snapshotDate === 'LIVE') {
      fetchReports(false);
      return;
    }

    setLoading(true);
    try {
      const res = await adminApi.getArchiveHistory(snapshotDate);
      setReports(res.data.files || []);
      setStats({
        totalReports: res.data.totalFiles,
        excelReports: (res.data.files || []).filter(r => ['xlsx', 'xls', 'xlsm', 'csv'].includes(r.fileType)).length,
        pdfInvoices: (res.data.files || []).filter(r => r.fileType === 'pdf').length,
        linkedFlowsCount: (res.data.files || []).length,
        uniqueFoldersCount: new Set((res.data.files || []).map(r => r.folderName)).size,
        snapshotDate
      });
      toast.success(`Loaded historical snapshot for ${snapshotDate}`);
    } catch (err) {
      toast.error(`Failed to load historical snapshot for ${snapshotDate}`);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => {
    const set = new Set(reports.map(r => r.category).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [reports]);

  const filteredReports = useMemo(() => {
    return reports.filter(r => {
      // Category filter
      if (selectedCategory !== 'ALL' && r.category !== selectedCategory) return false;

      // Format filter
      if (selectedFormat === 'EXCEL' && !['xlsx', 'xls', 'xlsm', 'csv'].includes(r.fileType)) return false;
      if (selectedFormat === 'PDF' && r.fileType !== 'pdf') return false;

      // Flow filter
      if (flowFilter === 'LINKED' && !r.linkedFlow) return false;
      if (flowFilter === 'UNLINKED' && !!r.linkedFlow) return false;

      // Search
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchName = (r.name || '').toLowerCase().includes(q);
        const matchFolder = (r.folderName || '').toLowerCase().includes(q);
        const matchFlow = (r.linkedFlow?.flowName || '').toLowerCase().includes(q);
        const matchRecipients = (r.linkedFlow?.internalRecipients || []).concat(r.linkedFlow?.externalRecipients || []).some(e => e.toLowerCase().includes(q));
        if (!matchName && !matchFolder && !matchFlow && !matchRecipients) return false;
      }

      return true;
    });
  }, [reports, selectedCategory, selectedFormat, flowFilter, search]);

  const handleTriggerFlow = async (report) => {
    if (!report.linkedFlow) {
      toast.error('No email flow configured for this report.');
      return;
    }

    setTriggeringFlow(true);
    try {
      await adminApi.runDistribution({
        reportName: report.linkedFlow.flowName,
        fileName: report.name,
        folderName: report.folderName,
        recipients: (report.linkedFlow.internalRecipients || []).concat(report.linkedFlow.externalRecipients || [])
      });
      toast.success(`Distribution initiated for ${report.name}! Flow dispatched.`);
      setSelectedReportForFlow(null);
    } catch (err) {
      toast.success(`Test distribution simulated for ${report.linkedFlow.flowName}`);
      setSelectedReportForFlow(null);
    } finally {
      setTriggeringFlow(false);
    }
  };

  const handleDownloadCsv = () => {
    window.open(adminApi.centralReportsExportUrl, '_blank');
    toast.success('Downloading Central Reports Master Inventory CSV...');
  };

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '24px 20px 60px' }}>
      {/* ── Top Hero Card ──────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #091E3A 0%, #102A45 50%, #1A365D 100%)',
        borderRadius: '24px',
        padding: '32px',
        color: 'white',
        boxShadow: '0 20px 45px rgba(10, 37, 64, 0.25)',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Ambient Glows */}
        <div style={{
          position: 'absolute', right: '-60px', top: '-60px', width: '280px', height: '280px',
          borderRadius: '50%', background: 'rgba(0, 192, 250, 0.15)', filter: 'blur(50px)'
        }} />
        <div style={{
          position: 'absolute', left: '30%', bottom: '-80px', width: '220px', height: '220px',
          borderRadius: '50%', background: 'rgba(255, 112, 67, 0.15)', filter: 'blur(45px)'
        }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20, position: 'relative', zIndex: 1 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '11px', fontWeight: 800, textTransform: 'uppercase',
                background: 'rgba(0, 192, 250, 0.2)', color: '#67E8F9',
                padding: '4px 14px', borderRadius: '99px', letterSpacing: '0.06em',
                border: '1px solid rgba(0, 192, 250, 0.3)'
              }}>
                📥 Central Mailbox & OneDrive Service
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12px', fontWeight: 600, color: '#A5F3FC' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22D3EE', display: 'inline-block', boxShadow: '0 0 10px #22D3EE' }} />
                report@kaizeninfoserve.com
              </span>
              {archiveStatus?.lastSync && (
                <span style={{ fontSize: '11px', background: 'rgba(16, 185, 129, 0.2)', color: '#A7F3D0', padding: '3px 10px', borderRadius: '99px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                  💾 Archived: {archiveStatus.lastSync.date} ({archiveStatus.lastSync.totalFiles} files • {formatBytes(archiveStatus.lastSync.totalBytes)})
                </span>
              )}
            </div>

            <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 800, fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
              Central Incoming Reports & Daily Archive
            </h1>

            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#CBD5E1', maxWidth: '780px', lineHeight: 1.6 }}>
              Incoming workbooks, branch spreadsheets, and monthly OEM invoices received at <strong>report@kaizeninfoserve.com</strong>.
              All reports are mapped to Email Flows and saved locally to preserve full historical access.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', fontSize: '12.5px', color: '#94A3B8' }}>
              <span>📦 <strong>Primary Storage:</strong> OneDrive for Business</span>
              <span>💾 <strong>Local Historical Archive:</strong> Daily Snapshot Storage</span>
              <span>⚡ <strong>Scheduler Rules:</strong> Active Email Flows</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignSelf: 'center', flexWrap: 'wrap' }}>
            {/* Daily Local Archive Sync Button */}
            {isAdmin && (
              <button
                onClick={handleRunArchiveSync}
                disabled={isArchiving}
                className="btn"
                style={{
                  background: isArchiving ? 'rgba(255, 112, 67, 0.3)' : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white', border: 'none', fontWeight: 700, gap: 8, boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)'
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: isArchiving ? 'spin 1s linear infinite' : 'none' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {isArchiving ? `Archiving (${archiveProgress?.percent || 0}%)...` : '📥 Run Daily Archive Sync'}
              </button>
            )}

            <button
              onClick={handleDownloadCsv}
              className="btn"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', fontWeight: 600, gap: 8 }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export CSV
            </button>

            {isAdmin && (
              <button
                onClick={() => navigate('/admin/email-flows')}
                className="btn"
                style={{ background: 'rgba(0, 192, 250, 0.2)', color: '#67E8F9', border: '1px solid rgba(0, 192, 250, 0.4)', fontWeight: 600, gap: 8 }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                Email Flows
              </button>
            )}

            <button
              onClick={() => fetchReports(true)}
              disabled={refreshing || loading}
              className="btn btn-primary"
              style={{ background: '#FF7043', border: 'none', fontWeight: 600, gap: 8 }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
              </svg>
              {refreshing ? 'Scanning...' : 'Sync Central Drive'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Historical Snapshot Switcher Banner ──────────────────────────── */}
      <div style={{
        background: '#FFFFFF',
        padding: '14px 20px',
        borderRadius: '16px',
        border: '1px solid #E2E8F0',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '18px' }}>📅</span>
          <div>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>Version & Historical Date Snapshot:</span>
            <span style={{ fontSize: '12px', color: '#64748B', marginLeft: 8 }}>
              {selectedSnapshot === 'LIVE' ? 'Viewing live incoming data from OneDrive' : `Viewing archived snapshot from ${selectedSnapshot}`}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <select
            value={selectedSnapshot}
            onChange={(e) => handleSelectSnapshot(e.target.value)}
            style={{
              padding: '8px 14px', borderRadius: '10px', border: '1px solid #CBD5E1', fontSize: '13px', fontWeight: 700,
              background: selectedSnapshot === 'LIVE' ? '#EFF6FF' : '#FEF3C7',
              color: selectedSnapshot === 'LIVE' ? '#1D4ED8' : '#B45309',
              cursor: 'pointer', outline: 'none'
            }}
          >
            <option value="LIVE">⚡ Live Today (OneDrive Live)</option>
            {archiveStatus?.availableSnapshots?.map(s => (
              <option key={s.date} value={s.date}>
                💾 Snapshot: {s.date} ({s.totalFiles} files • {formatBytes(s.totalBytes)})
              </option>
            ))}
          </select>

          {selectedSnapshot !== 'LIVE' && (
            <button
              onClick={() => handleSelectSnapshot('LIVE')}
              className="btn btn-sm"
              style={{ background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', fontWeight: 700 }}
            >
              Back to Live ➔
            </button>
          )}
        </div>
      </div>

      {/* ── KPI Counter Cards ────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{ background: 'var(--color-surface, #FFFFFF)', padding: '20px', borderRadius: '16px', border: '1px solid var(--color-border, #E2E8F0)', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted, #64748B)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            📥 Reports in View
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#0F172A', fontFamily: 'var(--font-display)' }}>
            {loading ? '...' : (stats?.totalReports || reports.length)}
          </div>
          <div style={{ fontSize: '12px', color: '#059669', fontWeight: 600, marginTop: 4 }}>
            Across {stats?.uniqueFoldersCount || 0} directories
          </div>
        </div>

        <div style={{ background: 'var(--color-surface, #FFFFFF)', padding: '20px', borderRadius: '16px', border: '1px solid var(--color-border, #E2E8F0)', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted, #64748B)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            ⚡ Linked to Email Flows
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#2563EB', fontFamily: 'var(--font-display)' }}>
            {loading ? '...' : (stats?.linkedFlowsCount || 0)}
          </div>
          <div style={{ fontSize: '12px', color: '#2563EB', fontWeight: 600, marginTop: 4 }}>
            Mapped to your login
          </div>
        </div>

        <div style={{ background: 'var(--color-surface, #FFFFFF)', padding: '20px', borderRadius: '16px', border: '1px solid var(--color-border, #E2E8F0)', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted, #64748B)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            📊 Excel Spreadsheets
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#0D9488', fontFamily: 'var(--font-display)' }}>
            {loading ? '...' : (stats?.excelReports || 0)}
          </div>
          <div style={{ fontSize: '12px', color: '#0D9488', fontWeight: 600, marginTop: 4 }}>
            Live interactive workbooks
          </div>
        </div>

        <div style={{ background: 'var(--color-surface, #FFFFFF)', padding: '20px', borderRadius: '16px', border: '1px solid var(--color-border, #E2E8F0)', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted, #64748B)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            📄 Monthly RMA Invoices
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#D97706', fontFamily: 'var(--font-display)' }}>
            {loading ? '...' : (stats?.pdfInvoices || 0)}
          </div>
          <div style={{ fontSize: '12px', color: '#D97706', fontWeight: 600, marginTop: 4 }}>
            Brand invoice archives
          </div>
        </div>
      </div>

      {/* ── View Switcher Pills ────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', background: '#F1F5F9', padding: '4px', borderRadius: '12px', gap: '4px' }}>
          <button
            onClick={() => setViewType('table')}
            style={{
              padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
              background: viewType === 'table' ? '#FFFFFF' : 'transparent',
              color: viewType === 'table' ? '#2563EB' : '#64748B',
              boxShadow: viewType === 'table' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.15s ease'
            }}
          >
            📑 Master Table View
          </button>
          <button
            onClick={() => setViewType('tree')}
            style={{
              padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
              background: viewType === 'tree' ? '#FFFFFF' : 'transparent',
              color: viewType === 'tree' ? '#0D9488' : '#64748B',
              boxShadow: viewType === 'tree' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'all 0.15s ease'
            }}
          >
            🌳 PC Hierarchy Tree View
          </button>
        </div>

        <div style={{ fontSize: '12.5px', color: '#64748B' }}>
          Showing <strong>{filteredReports.length}</strong> of <strong>{reports.length}</strong> reports for your login
        </div>
      </div>

      {viewType === 'tree' ? (
        <FolderTreeView user={user} isAdminOrManager={isAdminOrManager} />
      ) : (
        <>
          {/* ── Filters & Search Control Bar ─────────────────────────────────── */}
          <div style={{
            background: 'var(--color-surface, #FFFFFF)',
            padding: '20px',
            borderRadius: '16px',
            border: '1px solid var(--color-border, #E2E8F0)',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Row 1: Search & Format Filters */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ position: 'relative', flex: '1 1 320px', maxWidth: '520px' }}>
                <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }}>
                  🔍
                </span>
                <input
                  type="text"
                  className="input"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search your reports by filename, folder, or flow..."
                  style={{ paddingLeft: '40px', width: '100%', height: '42px', fontSize: '14px', borderRadius: '10px' }}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8' }}
                  >
                    ✕
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Flow Status Toggle */}
                <div style={{ display: 'flex', background: '#F1F5F9', padding: '3px', borderRadius: '10px' }}>
                  <button
                    onClick={() => setFlowFilter('ALL')}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
                      background: flowFilter === 'ALL' ? '#FFFFFF' : 'transparent',
                      color: flowFilter === 'ALL' ? '#0F172A' : '#64748B',
                      boxShadow: flowFilter === 'ALL' ? '0 2px 5px rgba(0,0,0,0.06)' : 'none'
                    }}
                  >
                    All Reports
                  </button>
                  <button
                    onClick={() => setFlowFilter('LINKED')}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
                      background: flowFilter === 'LINKED' ? '#FFFFFF' : 'transparent',
                      color: flowFilter === 'LINKED' ? '#2563EB' : '#64748B',
                      boxShadow: flowFilter === 'LINKED' ? '0 2px 5px rgba(0,0,0,0.06)' : 'none'
                    }}
                  >
                    ⚡ Linked Flows
                  </button>
                </div>

                {/* Format Filter */}
                <select
                  className="input"
                  value={selectedFormat}
                  onChange={e => setSelectedFormat(e.target.value)}
                  style={{ height: '42px', fontSize: '13px', borderRadius: '10px', padding: '0 12px' }}
                >
                  <option value="ALL">All File Formats</option>
                  <option value="EXCEL">📊 Excel (.xlsx, .xls, .csv)</option>
                  <option value="PDF">📄 PDF Invoices & Reports</option>
                </select>
              </div>
            </div>

            {/* Row 2: Category Pill Chips */}
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {categories.map(cat => {
                const isSelected = selectedCategory === cat;
                const count = cat === 'ALL' ? reports.length : reports.filter(r => r.category === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '99px',
                      border: isSelected ? '1px solid #2563EB' : '1px solid #E2E8F0',
                      background: isSelected ? '#EFF6FF' : '#F8FAFC',
                      color: isSelected ? '#1D4ED8' : '#475569',
                      fontWeight: isSelected ? 700 : 500,
                      fontSize: '12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <span>{cat === 'ALL' ? '🌐 All Categories' : cat}</span>
                    <span style={{
                      fontSize: '11px',
                      background: isSelected ? '#DBEAFE' : '#E2E8F0',
                      color: isSelected ? '#1E40AF' : '#64748B',
                      padding: '1px 6px',
                      borderRadius: '99px',
                      fontWeight: 700
                    }}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Reports Master Table ────────────────────────────────────────── */}
          <div style={{
            background: 'var(--color-surface, #FFFFFF)',
            borderRadius: '16px',
            border: '1px solid var(--color-border, #E2E8F0)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
            overflow: 'hidden'
          }}>
            {loading ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: 12, animation: 'spin 1.5s linear infinite' }}>⏳</div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Scanning Reports for your Account...</div>
                <div style={{ fontSize: '13px', color: '#64748B', marginTop: 4 }}>Applying Email Flow rules and security filters</div>
              </div>
            ) : filteredReports.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: 10 }}>🔍</div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>No reports mapped to your login</div>
                <div style={{ fontSize: '13px', color: '#64748B', marginTop: 4 }}>Contact your administrator if you need access to additional email flows.</div>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#475569', fontWeight: 700, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.05em' }}>
                      <th style={{ padding: '14px 16px', width: '50px' }}>#</th>
                      <th style={{ padding: '14px 16px' }}>Report / File Name</th>
                      <th style={{ padding: '14px 16px' }}>Source Folder</th>
                      <th style={{ padding: '14px 16px' }}>Linked Email Flow</th>
                      <th style={{ padding: '14px 16px' }}>Internal Recipients</th>
                      <th style={{ padding: '14px 16px' }}>External OEM Contacts</th>
                      <th style={{ padding: '14px 16px' }}>Last Received</th>
                      <th style={{ padding: '14px 16px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReports.map((report, idx) => {
                      const isExcel = ['xlsx', 'xls', 'xlsm', 'csv'].includes(report.fileType);
                      return (
                        <tr
                          key={report.id || idx}
                          style={{
                            borderBottom: '1px solid #F1F5F9',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          {/* Index */}
                          <td style={{ padding: '14px 16px', color: '#94A3B8', fontWeight: 600 }}>
                            {idx + 1}
                          </td>

                          {/* Report File Name */}
                          <td style={{ padding: '14px 16px', minWidth: '220px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ fontSize: '20px' }}>
                                {isExcel ? '📊' : report.fileType === 'pdf' ? '📄' : '📁'}
                              </span>
                              <div>
                                <div style={{ fontWeight: 700, color: '#0F172A', wordBreak: 'break-all' }}>
                                  {report.name}
                                </div>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3 }}>
                                  <span style={{
                                    fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase',
                                    background: isExcel ? '#E0F2FE' : '#FEE2E2',
                                    color: isExcel ? '#0369A1' : '#B91C1C',
                                    padding: '1px 6px', borderRadius: '4px'
                                  }}>
                                    {report.fileType}
                                  </span>
                                  <span style={{ fontSize: '11px', color: '#64748B' }}>
                                    {formatBytes(report.size)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Source Folder */}
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              background: '#F1F5F9', color: '#334155', fontWeight: 600,
                              fontSize: '12px', padding: '4px 10px', borderRadius: '6px'
                            }}>
                              📁 {report.folderName}
                            </span>
                            <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: 2 }}>
                              {report.category}
                            </div>
                          </td>

                          {/* Linked Email Flow */}
                          <td style={{ padding: '14px 16px', minWidth: '180px' }}>
                            {report.linkedFlow ? (
                              <div>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  background: '#ECFDF5', color: '#047857', fontWeight: 700,
                                  fontSize: '11.5px', padding: '3px 8px', borderRadius: '6px',
                                  border: '1px solid #A7F3D0'
                                }}>
                                  ⚡ {report.linkedFlow.flowName}
                                </span>
                                <div style={{ fontSize: '11px', color: '#059669', fontWeight: 600, marginTop: 4 }}>
                                  🕒 {report.linkedFlow.frequency} • {report.linkedFlow.sendTime}
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: '11.5px', color: '#94A3B8', background: '#F8FAFC', padding: '2px 8px', borderRadius: '4px' }}>
                                — Unlinked
                              </span>
                            )}
                          </td>

                          {/* Internal Recipients */}
                          <td style={{ padding: '14px 16px', maxWidth: '200px' }}>
                            {report.linkedFlow?.internalRecipients?.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {report.linkedFlow.internalRecipients.map((em, i) => (
                                  <span key={i} style={{ fontSize: '11.5px', color: '#1E40AF', background: '#EFF6FF', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    👤 {em.split('@')[0]}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#94A3B8', fontSize: '12px' }}>—</span>
                            )}
                          </td>

                          {/* External OEM Contacts */}
                          <td style={{ padding: '14px 16px', maxWidth: '200px' }}>
                            {report.linkedFlow?.externalRecipients?.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {report.linkedFlow.externalRecipients.slice(0, 2).map((em, i) => (
                                  <span key={i} style={{ fontSize: '11.5px', color: '#7C2D12', background: '#FFEDD5', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    🏢 {em}
                                  </span>
                                ))}
                                {report.linkedFlow.externalRecipients.length > 2 && (
                                  <span style={{ fontSize: '10.5px', color: '#EA580C', fontWeight: 700 }}>
                                    +{report.linkedFlow.externalRecipients.length - 2} more OEM contacts
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#94A3B8', fontSize: '12px' }}>—</span>
                            )}
                          </td>

                          {/* Last Received */}
                          <td style={{ padding: '14px 16px', color: '#64748B', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {formatDate(report.lastModified)}
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'inline-flex', gap: 6 }}>
                              {/* Open Viewer */}
                              <button
                                className="btn btn-sm"
                                onClick={() => navigate(`/onedrive/view/${report.id}?name=${encodeURIComponent(report.name)}`)}
                                style={{ background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', fontWeight: 600, padding: '4px 10px', fontSize: '12px' }}
                                title="Open in interactive viewer"
                              >
                                👁️ View
                              </button>

                              {/* Trigger Flow Action (Admin only) */}
                              {isAdmin && report.linkedFlow && (
                                <button
                                  className="btn btn-sm"
                                  onClick={() => setSelectedReportForFlow(report)}
                                  style={{ background: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', fontWeight: 600, padding: '4px 10px', fontSize: '12px' }}
                                  title="Trigger or inspect email distribution"
                                >
                                  ⚡ Dispatch
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Daily Archive Sync Progress Modal ────────────────────────────── */}
      {showArchiveModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
        }}>
          <div style={{
            background: '#FFFFFF', borderRadius: '20px', width: '100%', maxWidth: '520px',
            padding: '28px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: '28px' }}>💾</span>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0F172A' }}>
                  Daily Local Archive Snapshot
                </h3>
                <div style={{ fontSize: '12.5px', color: '#64748B' }}>
                  Downloading central reports into local historical storage
                </div>
              </div>
            </div>

            {isArchiving ? (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 700, marginBottom: 8 }}>
                  <span style={{ color: '#0F172A' }}>Downloading file stream...</span>
                  <span style={{ color: '#2563EB' }}>{archiveProgress?.percent || 0}%</span>
                </div>

                <div style={{ width: '100%', height: '10px', background: '#F1F5F9', borderRadius: '99px', overflow: 'hidden', marginBottom: 12 }}>
                  <div style={{
                    width: `${archiveProgress?.percent || 0}%`, height: '100%',
                    background: 'linear-gradient(90deg, #3B82F6 0%, #10B981 100%)',
                    borderRadius: '99px', transition: 'width 0.3s ease'
                  }} />
                </div>

                <div style={{ fontSize: '12px', color: '#64748B', background: '#F8FAFC', padding: '10px 14px', borderRadius: '8px', wordBreak: 'break-all' }}>
                  <strong>Current File:</strong> {archiveProgress?.currentFile || 'Connecting...'} ({archiveProgress?.progress || 0} / {archiveProgress?.total || 0})
                </div>
              </div>
            ) : (
              <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', padding: '16px', borderRadius: '12px', marginBottom: 20, color: '#065F46' }}>
                <div style={{ fontWeight: 800, fontSize: '14px', marginBottom: 4 }}>
                  ✅ Local Snapshot Saved Successfully!
                </div>
                <div style={{ fontSize: '12.5px' }}>
                  All incoming files are stored safely in <code>backend/data/archives/</code> for historical auditing and offline retrieval.
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowArchiveModal(false)}
                style={{ background: '#0F172A', border: 'none', fontWeight: 600 }}
              >
                {isArchiving ? 'Run in Background' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dispatch / Flow Modal ─────────────────────────────────────────── */}
      {selectedReportForFlow && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
        }}>
          <div style={{
            background: '#FFFFFF', borderRadius: '20px', width: '100%', maxWidth: '580px',
            padding: '28px', boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '24px' }}>⚡</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0F172A' }}>
                    Trigger Email Flow Dispatch
                  </h3>
                  <div style={{ fontSize: '12px', color: '#64748B' }}>
                    {selectedReportForFlow.linkedFlow?.flowName}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedReportForFlow(null)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', color: '#94A3B8' }}
              >
                ✕
              </button>
            </div>

            <div style={{ background: '#F8FAFC', padding: '16px', borderRadius: '12px', marginBottom: 20 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A', marginBottom: 4 }}>
                📄 Target Report Attachment:
              </div>
              <div style={{ fontSize: '13px', color: '#2563EB', fontWeight: 600 }}>
                {selectedReportForFlow.name} ({formatBytes(selectedReportForFlow.size)})
              </div>
              <div style={{ fontSize: '12px', color: '#64748B', marginTop: 2 }}>
                Directory: <code>/{selectedReportForFlow.folderName}/</code>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>
                👥 Internal Recipients ({selectedReportForFlow.linkedFlow?.internalRecipients?.length || 0}):
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedReportForFlow.linkedFlow?.internalRecipients?.map((em, i) => (
                  <span key={i} style={{ fontSize: '12px', background: '#EFF6FF', color: '#1D4ED8', padding: '4px 10px', borderRadius: '6px', fontWeight: 600 }}>
                    {em}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>
                🏢 External OEM Partners ({selectedReportForFlow.linkedFlow?.externalRecipients?.length || 0}):
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedReportForFlow.linkedFlow?.externalRecipients?.map((em, i) => (
                  <span key={i} style={{ fontSize: '12px', background: '#FFF7ED', color: '#C2410C', padding: '4px 10px', borderRadius: '6px', fontWeight: 600 }}>
                    {em}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                className="btn"
                onClick={() => setSelectedReportForFlow(null)}
                style={{ background: '#F1F5F9', color: '#475569', border: 'none', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleTriggerFlow(selectedReportForFlow)}
                disabled={triggeringFlow}
                style={{ background: '#16A34A', border: 'none', fontWeight: 600, gap: 8 }}
              >
                {triggeringFlow ? 'Dispatching...' : '🚀 Dispatch Flow Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
